import { useQueryClient } from '@tanstack/react-query';
import { TagPreference } from '@hungryroot/page-food-profile';
import { useGetPreferenceOptions } from '@hungryroot/preference-options';
import { type LikeDislikeTagsRatings } from '@hungryroot/rest';
import {
  type PreferenceOption,
  type PreferenceOptionKey,
} from '@hungryroot/rest/api/v2/preference_options/get/get_preference_options';
import { getCustomerTagsQueryKey, useCustomerTags } from '@hungryroot/tags';
import {
  type TagPayload,
  usePatchCustomerTagSelf,
} from '../../../api/v2/customers/[id]/tags/patch/patch-customer-tags';

type TagPreferenceTuple = [
  LikeDislikeTagsRatings | undefined,
  PreferenceOption,
];

interface FoodProfilePageParams {
  customerId: number;
  page: PreferenceOptionKey;
  tagExclusions?: number[];
}

/** Drives the content for a food profile section that uses the `preference_options` endpoint */
export function useFoodProfilePage({
  customerId,
  page,
  tagExclusions,
}: FoodProfilePageParams) {
  /** Gets the tags that we want to render for all pages */
  const pagesData = useGetPreferenceOptions();

  /** List of tags that should be displayed as a switch on the selected page */
  const tagsForPageDisplay = pagesData?.data?.pages
    .find((pageData) => pageData.key === page)
    ?.options?.filter((option) => !tagExclusions?.includes(option.tag_id));

  const tagsToRequest = tagsForPageDisplay?.map((option) => option.tag_id);

  /* Get the customers preference data for the tags that we want to render */
  const { data: customerTags } = useCustomerTags(tagsToRequest);

  const usePatchCustomerTagSelfMutation = usePatchCustomerTagSelf();

  const preferencesAndTags: undefined | TagPreferenceTuple[] =
    tagsForPageDisplay?.map((displayTag) => [
      customerTags?.results.find((data) => data.tag === displayTag.tag_id)
        ?.preference,
      displayTag,
    ]);

  const queryClient = useQueryClient();

  const onSwitchSelect = (
    tag: Pick<PreferenceOption, 'tag_id'>,
    selected: boolean
  ) => {
    const payload: TagPayload = {
      customer_id: customerId,
      tag: tag.tag_id,
      preference: selected ? TagPreference.Selected : TagPreference.NotSelected,
    };

    return usePatchCustomerTagSelfMutation.mutate(
      { data: [payload] },
      {
        onSuccess: () => {
          const key = getCustomerTagsQueryKey(tagsToRequest);
          queryClient.invalidateQueries({
            queryKey: key,
          });
        },
      }
    );
  };

  const subtitle = customerTags?.results
    .filter((pref) => pref.preference === TagPreference.Selected)
    .map(
      (tag) =>
        tagsForPageDisplay?.find(
          (pageDisplayTag) => pageDisplayTag.tag_id === tag.tag
        )?.name
    )
    .filter(Boolean)
    .join(', ');

  return {
    subtitle,
    preferencesAndTags,
    onSwitchSelect,
  };
}
